import _ from 'underscore';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { FetchPolicy } from '@bingads-webui-react/overreact';
import { useCampaign } from '@bingads-webui/overreact-odata-multi-channel';
import { useCampaignVariables, usePostponeReadVariables, ALL_CAMPAIGN_OPTIONS, DEFAULT_CAMPAIGN_OPTIONS } from './use-variables';

export function useStoreCampaign({
  campaignId,
  isCampaignReady,
}) {
  const campaignVariables = useCampaignVariables(campaignId, ALL_CAMPAIGN_OPTIONS);
  const campaignConfig = usePostponeReadVariables();
  const hookResult = useCampaign(campaignVariables, campaignConfig);
  const [
    { data: campaign, error: campaignError },
    { refetch: readCampaign },
  ] = hookResult;

  useEffect(() => {
    // Kick off readCampaign, so this hook receives updates
    if (campaignId && !campaign && !campaignError && isCampaignReady) {
      readCampaign({
        variables: { options: { fetchPolicy: FetchPolicy.StoreOnly } },
      });
    }
  }, [campaign, campaignError, campaignId, isCampaignReady, readCampaign]);

  return hookResult;
}

export function useCampaignSimpleRead({
  campaignId,
  isCampaignReady,
  campaignOptions = DEFAULT_CAMPAIGN_OPTIONS,
  lookupCacheByVariables = undefined,
  isForceNetworkRead = false,
  onReadComplete = _.noop,
  onReadError = _.noop,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const campaignVariables = useCampaignVariables(campaignId);
  const campaignConfig = usePostponeReadVariables(lookupCacheByVariables);
  const hookResult = useCampaign(campaignVariables, campaignConfig);
  const [
    { data: campaign, error: campaignError },
    { refetch: readCampaign, update, destroy },
  ] = hookResult;

  const readVariables = useMemo(() => {
    if (isForceNetworkRead) {
      return {
        variables: { options: { fetchPolicy: FetchPolicy.NetworkOnly } },
      };
    }
    return {};
  }, [isForceNetworkRead]);

  const onComplete = useCallback((response) => {
    setIsLoading(false);
    if (_.isFunction(onReadComplete)) {
      onReadComplete(response);
    }
  }, [onReadComplete]);

  const onError = useCallback((error) => {
    setIsLoading(false);
    if (_.isFunction(onReadError)) {
      onReadError(error);
    }
  }, [onReadError]);

  const readWithOptions = useCallback((parameter = {}, ...rest) => readCampaign({
    variables: {
      ...campaignOptions,
      ...parameter.variables,
    },
  }, ...rest), [campaignOptions, readCampaign]);

  useEffect(() => {
    // Kick off readCampaign, so this hook receives updates
    if (campaignId && isCampaignReady) {
      if (!campaign && !campaignError && !isLoading) {
        setIsLoading(true);
        readWithOptions(readVariables, {
          onComplete,
          onError,
        });
      }
    }
  }, [campaign, campaignError, campaignId, isCampaignReady, isLoading,
    onComplete, onError, readVariables, readWithOptions]);

  return [{ data: campaign, error: campaignError, isLoading },
    { refetch: readWithOptions, update, destroy }];
}

export const useCampaignWithId = (campaignId) => {
  const [lastCampaignId, setLastCampaignId] = useState(null);

  const campaignVariables = useCampaignVariables(campaignId);
  const config = useMemo(() => ({ fetch: { postponeRead: true } }), []);

  const hookResult = useCampaign(campaignVariables, config);
  const [, { refetch: read }] = hookResult;

  // Work around for issue: data not updated with campaign id change.
  useEffect(() => {
    if (campaignId && campaignId !== lastCampaignId) {
      setLastCampaignId(campaignId);
      read();
    }
  }, [lastCampaignId, campaignId, read]);

  return hookResult;
};
